import React, { useEffect, useState } from "react";
import { getUser, deleteUserDestination } from "../../state/User";
import Loading from "../utils/Loading";
import { TwitterLink } from "./Twitter";
import Kebab from "../utils/Kebab";
import { areYouSure, formatDestination } from "../helpers";
import { DestinationType } from "../../constants";
import Server from "../utils/icons/Server";
import { getLinkedInTokenExpiry } from "./LinkedIn";

export default function Destination({
  destination,
}: {
  destination: Destination;
  key: any;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [_, VerifyingLinkedInUI] = getLinkedInTokenExpiry(destination);
  const user = getUser();
  let meta;
  const errorComp = error && (
    <span className="error ml1 block">
      ⚠️ Error deleting this destination. Please try again later.
    </span>
  );

  function getKebabMenuItems(destination) {
    return [
      {
        label: "Delete",
        onClick: areYouSure(async () => {
          setDeleting(true);
          try {
            if (user) {
              await deleteUserDestination(destination.id);
            }
            setSuccess(true);
            setDeleting(false);
          } catch (err) {
            console.error(err);
            setDeleting(false);
            setError(true);
          }
        }),
      },
    ];
  }

  if (deleting) {
    return (
      <li>
        <div>
          <Loading size={35} />
        </div>
        <div className="pl1">Deleting ...</div>
      </li>
    );
  }
  if (success) {
    return (
      <li>
        <div className="p1 success">✅ Destination deleted successfully.</div>
      </li>
    );
  }

  switch (destination.type) {
    case DestinationType.TWITTER:
      meta = destination.meta as DestinationTwitterMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/twitter.png"
              alt="twitter"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            <TwitterLink handle={meta.handle} />
            <br />
            <small>Connected Twitter account.</small>
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.FACEBOOK:
      meta = destination.meta as DestinationFacebookMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/facebook.png"
              alt="facebook"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            {meta.name}
            <br />
            <small>Connected Facebook page.</small>
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.MAILCHIMP:
      meta = destination.meta as DestinationMailchimpMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/mailchimp.png"
              alt="mailchimp"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            {meta.name}
            <br />
            <small>Connected Mailchimp audience.</small>
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.INSTAGRAM:
      meta = destination.meta as DestinationInstagramMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/instagram.png"
              alt="instagram"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            <a href={`https://www.instagram.com/${meta.name}`} target="_blank">
              @{meta.name}
            </a>
            <br />
            <small>Connected Instagram account.</small>
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.YOUTUBE:
      meta = destination.meta as DestinationYouTubeMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/youtube.png"
              alt="youtube"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            <a
              href={`https://www.youtube.com/channel/${meta.id}`}
              target="_blank"
            >
              {meta.name}
            </a>
            <br />
            <small>Connected YouTube account.</small>
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.CUSTOM_BACKEND:
      meta = destination.meta as DestinationCustomBackendMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/server.png"
              alt="custom backend"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            {meta.name}
            <br />
            <small>Connected Custom back-end.</small>
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.LINKEDIN:
      meta = destination.meta as DestinationLinkedinMeta;
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/linkedin.png"
              alt="linkedin"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            {meta.name}
            <br />
            <small>Connected LinkedIn account.</small>
            <br />
            {VerifyingLinkedInUI}
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
    case DestinationType.GROUP:
      meta = destination.meta as DestinationGroupMeta;
      let list = meta.destinationIds
        .map((id) => {
          const groupedD = (user.destinations || []).find((d) => d.id === id);
          if (groupedD) {
            return formatDestination(groupedD, "text", false);
          }
        })
        .filter(Boolean);
      // @ts-ignore
      return (
        <li>
          <div className="leading-image">
            <img
              src="/assets/img/group.png"
              alt="group"
              width={30}
              className="ib vam"
            />
          </div>
          <div>
            {meta.name}
            <br />
            <small>Group destination ({list})</small>
            <br />
            {errorComp}
          </div>
          <Kebab links={getKebabMenuItems(destination)} />
        </li>
      );
  }
  return <li>{destination.type}</li>;
}
